import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ALBARAN_STATUS } from '../types/albaran';

const DetalleAlbaran = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company } = useCompany();
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadRequest();
  }, [id, user, company]);

  const loadRequest = async () => {
    if (!id || !user || !company) return;

    try {
      setLoading(true);
      setError('');

      const docRef = doc(db, 'albaran_requests', id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setError('Solicitud de albarán no encontrada');
        return;
      }

      const requestData = {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate(),
        updatedAt: docSnap.data().updatedAt?.toDate(),
        emailSentAt: docSnap.data().emailSentAt?.toDate(),
        processedAt: docSnap.data().processedAt?.toDate()
      };

      // Verificar que pertenece a la misma empresa
      if (requestData.companyId !== company.id) {
        setError('No tienes permisos para ver esta solicitud');
        return;
      }

      setRequest(requestData);

    } catch (err) {
      console.error('Error loading albaran request:', err);
      setError('Error al cargar la solicitud de albarán');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      [ALBARAN_STATUS.ENVIADA]: 'badge bg-primary',
      [ALBARAN_STATUS.PROCESADA]: 'badge bg-warning text-dark',
      [ALBARAN_STATUS.ENTREGADA]: 'badge bg-success',
      'pendiente': 'badge bg-danger'
    };
    
    const labels = {
      [ALBARAN_STATUS.ENVIADA]: 'Enviada',
      [ALBARAN_STATUS.PROCESADA]: 'Procesada',
      [ALBARAN_STATUS.ENTREGADA]: 'Entregada',
      'pendiente': 'Pendiente de gestionar'
    };

    return (
      <span className={badges[status] || 'badge bg-secondary'}>
        {labels[status] || status}
      </span>
    );
  };

  const getOrigenBadge = (origen) => {
    if (origen === 'kanban-cejm') {
      return (
        <span className="badge bg-info" title="Solicitud recibida desde Kanban CEJM">
          🔗 Kanban CEJM
        </span>
      );
    }
    return (
      <span className="badge bg-secondary" title="Solicitud creada desde la app interna">
        📱 App interna
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDuplicate = () => {
    const params = new URLSearchParams({
      duplicate: request.id
    });
    navigate(`/solicitar-albaran?${params.toString()}`);
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || !request) return;
    
    try {
      setUpdatingStatus(true);
      
      // Actualizar en Firestore
      const requestRef = doc(db, 'albaran_requests', request.id);
      await updateDoc(requestRef, {
        status: newStatus,
        updatedAt: new Date(),
        ...(newStatus === ALBARAN_STATUS.PROCESADA && { processedAt: new Date() }),
        ...(newStatus === ALBARAN_STATUS.ENTREGADA && { deliveredAt: new Date() })
      });
      
      // Actualizar en el estado local
      setRequest(prevRequest => ({
        ...prevRequest,
        status: newStatus,
        updatedAt: new Date(),
        ...(newStatus === ALBARAN_STATUS.PROCESADA && { processedAt: new Date() }),
        ...(newStatus === ALBARAN_STATUS.ENTREGADA && { deliveredAt: new Date() })
      }));
      
      console.log(`Estado actualizado: ${request.id} -> ${newStatus}`);
      
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Error al actualizar el estado');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getNextStatus = (currentStatus) => {
    switch (currentStatus) {
      case ALBARAN_STATUS.ENVIADA:
        return ALBARAN_STATUS.PROCESADA;
      case ALBARAN_STATUS.PROCESADA:
        return ALBARAN_STATUS.ENTREGADA;
      default:
        return null;
    }
  };

  const getStatusButtonText = (currentStatus) => {
    switch (currentStatus) {
      case ALBARAN_STATUS.ENVIADA:
        return 'Marcar como Procesada';
      case ALBARAN_STATUS.PROCESADA:
        return 'Marcar como Entregada';
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2">Cargando detalles del albarán...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/historial-albaranes')}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Volver al Historial
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <i className="fas fa-box me-2"></i>
                📦 Detalle de Albarán - {request.numeroPedido}
              </h4>
              <div className="btn-group">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/historial-albaranes')}
                >
                  <i className="fas fa-arrow-left me-1"></i>
                  Volver
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  onClick={handlePrint}
                >
                  <i className="fas fa-print me-1"></i>
                  Imprimir
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-info"
                  onClick={handleDuplicate}
                >
                  <i className="fas fa-copy me-1"></i>
                  Duplicar
                </button>
                {getNextStatus(request.status) && (
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={() => handleStatusChange(getNextStatus(request.status))}
                    disabled={updatingStatus}
                    title={getStatusButtonText(request.status)}
                  >
                    {updatingStatus ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Actualizando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-arrow-right me-1"></i>
                        {getStatusButtonText(request.status)}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="card-body">
              {/* Estado y fechas */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5>Estado de la Solicitud</h5>
                  <p className="mb-2">
                    {getStatusBadge(request.status)}
                    {request.origen && (
                      <span className="ms-2">
                        {getOrigenBadge(request.origen)}
                      </span>
                    )}
                  </p>
                  <small className="text-muted">
                    Creada el {formatDate(request.fechaSolicitud || request.createdAt)}
                  </small>
                  {request.kanban_card_id && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <strong>ID Tarjeta Kanban:</strong> <code>{request.kanban_card_id}</code>
                      </small>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <h5>Información del Solicitante</h5>
                  <p className="mb-1">
                    <strong>{request.tecnicoSolicitante || request.requesterName}</strong>
                  </p>
                  {request.requesterEmail && (
                    <p className="mb-0">
                      <small className="text-muted">{request.requesterEmail}</small>
                    </p>
                  )}
                  {request.tecnicoResponsable && request.tecnicoResponsable !== request.tecnicoSolicitante && (
                    <p className="mb-0 mt-2">
                      <small><strong>Técnico Responsable:</strong> {request.tecnicoResponsable}</small>
                    </p>
                  )}
                </div>
              </div>

              <hr />

              {/* Información principal del albarán */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="mb-3">
                    <i className="fas fa-info-circle me-2"></i>
                    Información del Albarán
                  </h5>
                </div>
                
                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">Datos del Pedido</h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td><strong>Número de Pedido:</strong></td>
                            <td>{request.numeroPedido}</td>
                          </tr>
                          {request.numeroPresupuesto && (
                            <tr>
                              <td><strong>Nº Presupuesto:</strong></td>
                              <td>{request.numeroPresupuesto}</td>
                            </tr>
                          )}
                          <tr>
                            <td><strong>Técnico Responsable:</strong></td>
                            <td>{request.tecnicoResponsable}</td>
                          </tr>
                          <tr>
                            <td><strong>Destinatario:</strong></td>
                            <td>{request.nombreDestinatario || request.personaContacto}</td>
                          </tr>
                          {request.personaContacto && (
                            <tr>
                              <td><strong>Persona Contacto:</strong></td>
                              <td>{request.personaContacto}</td>
                            </tr>
                          )}
                          {request.telefonoContacto && (
                            <tr>
                              <td><strong>Teléfono:</strong></td>
                              <td>{request.telefonoContacto}</td>
                            </tr>
                          )}
                          {request.emailCliente && (
                            <tr>
                              <td><strong>Email Cliente:</strong></td>
                              <td>{request.emailCliente}</td>
                            </tr>
                          )}
                          {request.tipoTransporte && (
                            <tr>
                              <td><strong>Tipo Transporte:</strong></td>
                              <td>{request.tipoTransporte}</td>
                            </tr>
                          )}
                          {request.condicionesEspeciales && (
                            <tr>
                              <td><strong>Condiciones Especiales:</strong></td>
                              <td><em>{request.condicionesEspeciales}</em></td>
                            </tr>
                          )}
                          {request.observaciones && (
                            <tr>
                              <td><strong>Observaciones:</strong></td>
                              <td><em>{request.observaciones}</em></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">Datos del Material</h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td><strong>Número de Serie:</strong></td>
                            <td><code>{request.numeroSerie}</code></td>
                          </tr>
                          {request.tipoMaterial && (
                            <tr>
                              <td><strong>Tipo de Material:</strong></td>
                              <td>{request.tipoMaterial}</td>
                            </tr>
                          )}
                          {request.cantidad && (
                            <tr>
                              <td><strong>Cantidad:</strong></td>
                              <td>{request.cantidad}</td>
                            </tr>
                          )}
                          <tr>
                            <td><strong>Consignatario:</strong></td>
                            <td>{request.consignatario}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Información de entrega */}
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="mb-3">
                    <i className="fas fa-truck me-2"></i>
                    Información de Entrega
                  </h5>
                </div>
                
                <div className="col-md-8 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">Dirección de Entrega</h6>
                      <p className="mb-0">{request.direccionEntrega}</p>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3">
                  <div className="card bg-light">
                    <div className="card-body">
                      <h6 className="card-title">Contacto</h6>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td><strong>Persona:</strong></td>
                            <td>{request.personaContacto}</td>
                          </tr>
                          {request.telefonoContacto && (
                            <tr>
                              <td><strong>Teléfono:</strong></td>
                              <td>
                                <a href={`tel:${request.telefonoContacto}`}>
                                  {request.telefonoContacto}
                                </a>
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td><strong>Horario:</strong></td>
                            <td>{request.horarioRecepcion}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalles adicionales */}
              {(request.condicionesEspeciales || request.observaciones) && (
                <div className="row mb-4">
                  <div className="col-12">
                    <h5 className="mb-3">
                      <i className="fas fa-clipboard-list me-2"></i>
                      Detalles Adicionales
                    </h5>
                  </div>
                  
                  {request.condicionesEspeciales && (
                    <div className="col-md-6 mb-3">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-title">Condiciones Especiales</h6>
                          <p className="mb-0">{request.condicionesEspeciales}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {request.observaciones && (
                    <div className="col-md-6 mb-3">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-title">Observaciones</h6>
                          <p className="mb-0">{request.observaciones}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalleAlbaran;